/** Категории: список, тип и переименование.
 *
 * В чате категорию нельзя было ни увидеть списком, ни переименовать — создание
 * жило внутри правки конкретной записи.
 *
 * Главное здесь не имя, а тип: «нужное / хотелки / сбережения». Он заполнен у
 * сид-категорий и пуст у своих, поэтому траты по своим молча уходили в
 * «не размечено» и портили разбивку на экране «Накопления».
 */
import { useEffect, useState } from "react";
import { ApiError, api, type Budget, type CategoryFull, type CategoryPatch,
         type Failure } from "../lib/api.ts";
import { formatMoney } from "../lib/money.ts";
import { plural } from "../lib/plural.ts";
import { rowButton } from "../lib/rowStyle.ts";
import { CategoryIcon } from "../components/CategoryIcon.tsx";
import { ICON_KEYS } from "../lib/categoryIcon.ts";
import { nthColor } from "../lib/category.ts";
import { tap } from "../lib/telegram.ts";
import { Header } from "../components/Header.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { Fallback } from "./Fallback.tsx";
import { card, hint } from "../components/ui.tsx";
import { onRadioKeys } from "../lib/radiogroup.ts";

const BUCKETS = [
  { key: "need", label: "Нужное" },
  { key: "want", label: "Хотелки" },
  { key: "savings", label: "Сбережения" },
] as const;

export function CategoriesScreen({ onBack }: { onBack: () => void }) {
  const [kind, setKind] = useState<"expense" | "income">("expense");
  const [rows, setRows] = useState<CategoryFull[] | null>(null);
  const [failure, setFailure] = useState<Failure | null>(null);
  const [opened, setOpened] = useState<CategoryFull | null>(null);
  const [adding, setAdding] = useState(false);
  const [limits, setLimits] = useState<Budget[]>([]);

  const load = () => {
    setFailure(null);
    api.allCategories(kind).then(setRows)
      .catch((e) => setFailure(e instanceof ApiError ? e.kind : "broken"));
    // Лимиты не критичны: не пришли — строка про лимит просто не появится.
    api.budgets().then((b) => setLimits(b.items)).catch(() => setLimits([]));
  };

  useEffect(load, [kind]);

  if (failure) return <Fallback kind={failure} onRetry={load} />;

  const unmarked = (rows ?? []).filter((c) => c.bucket === null).length;

  return (
    <div style={{ padding: "var(--screen)" }}>
      <Header title="Категории" onBack={onBack} />

      <div role="radiogroup" onKeyDown={onRadioKeys} aria-label="Вид категорий" style={{ display: "flex",
                                   background: "var(--surface-2)",
                                   borderRadius: "var(--r-pill)", padding: 3,
                                   marginBottom: "var(--s-3)" }}>
        {(["expense", "income"] as const).map((k) => {
          const on = k === kind;
          return (
            <button key={k} type="button" role="radio" aria-checked={on}
                    onClick={() => { tap(); setKind(k); }}
                    style={{ flexGrow: 1, minHeight: 38, border: "none", cursor: "pointer",
                             font: "inherit", fontSize: "var(--f-body-s)",
                             fontWeight: on ? 600 : 400,
                             color: on ? "var(--text)" : "var(--text-muted)",
                             background: on ? "var(--surface-3)" : "transparent",
                             borderRadius: "var(--r-pill)",
                             boxShadow: on ? "var(--shadow-seg)" : "none" }}>
              {k === "expense" ? "Траты" : "Доходы"}
            </button>
          );
        })}
      </div>

      {rows === null ? <Skeleton /> : (
        <>
          <section style={{ ...card, padding: "2px var(--s-5)" }}>
            {rows.map((c, i) => (
              <button key={c.id} type="button" onClick={() => { tap(); setOpened(c); }}
                      style={rowButton(i === rows.length - 1)}>
                <CategoryIcon name={c.name} icon={c.icon} color={categoryColorOf(c, rows)} size={34} />
                <span style={{ flexGrow: 1, minWidth: 0 }}>
                  <span style={{ display: "block", fontSize: "var(--f-body)" }}>
                    {c.name}
                    {!c.is_seed && (
                      <span style={{ fontSize: "var(--f-micro)", color: "var(--text-faint)",
                                     background: "var(--surface-2)", borderRadius: 5,
                                     padding: "2px 6px", marginLeft: 7 }}>своя</span>
                    )}
                  </span>
                  {/* Тип — про разбивку 50/30/20, а она делит ТРАТЫ. У дохода
                      «нужное / хотелки / сбережения» не бывает, и «тип не задан»
                      под зарплатой требовал задать несуществующее. */}
                  {kind === "expense" && (
                    <span style={{ display: "block", fontSize: "var(--f-caption)",
                                   color: c.bucket ? "var(--text-muted)" : "var(--text-faint)",
                                   marginTop: 2 }}>
                      {BUCKETS.find((b) => b.key === c.bucket)?.label ?? "тип не задан"}
                    </span>
                  )}
                </span>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
                     stroke="var(--text-disabled)" strokeWidth="2" strokeLinecap="round"
                     strokeLinejoin="round" aria-hidden="true">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </button>
            ))}
          </section>

          <button type="button" onClick={() => { tap(); setAdding(true); }}
                  style={{ display: "flex", alignItems: "center", gap: "var(--s-3)",
                           width: "100%", minHeight: "var(--tap)", marginTop: "var(--s-3)",
                           padding: "12px var(--s-5)", borderRadius: "var(--r-card)",
                           border: "1px dashed var(--surface-3)", background: "none",
                           cursor: "pointer", font: "inherit", fontSize: "var(--f-body)",
                           color: "var(--accent)" }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            {kind === "expense" ? "Новая категория траты" : "Новая категория дохода"}
          </button>

          {unmarked > 0 && kind === "expense" && (
            <p style={hint}>
              У {plural(unmarked, ["категории", "категорий", "категорий"])} тип не задан —
              их траты не попадают в разбивку на экране «Накопления».
            </p>
          )}
        </>
      )}

      {adding && (
        <NewSheet kind={kind} onClose={() => setAdding(false)}
                  onDone={() => { setAdding(false); load(); }} />
      )}

      {opened && (
        <CategorySheet category={opened} kind={kind}
                       siblings={(rows ?? []).filter((c) => c.id !== opened.id)}
                       budget={limits.find((b) => b.category_id === opened.id) ?? null}
                       onClose={() => { setOpened(null); load(); }}
                       onDone={() => { setOpened(null); load(); }}
                       onLimitChanged={load} />
      )}
    </div>
  );
}

/** Новая своя категория.
 *
 *  ТИП СПРАШИВАЕМ СРАЗУ, а не потом в правке: у своих категорий он пустой, и
 *  траты по ним молча уходят в «не размечено», портя разбивку 50/30/20. Один
 *  тап здесь дешевле похода по списку за неразмеченными позже.
 *
 *  У ДОХОДА ТИПА НЕТ: разбивка делит траты (см. `app/domain/methodology.py`).
 */
function NewSheet({ kind, onClose, onDone }: {
  kind: "expense" | "income"; onClose: () => void; onDone: () => void;
}) {
  const [name, setName] = useState("");
  const [bucket, setBucket] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");

  const create = () => {
    if (busy || !name.trim()) return;
    tap();
    setBusy(true);
    setTrouble("");
    api.createCategory({
      kind, name: name.trim(), ...(bucket ? { bucket } : {}),
    })
      .then(onDone)
      .catch((e) => setTrouble(
        e instanceof ApiError && e.kind === "taken"
          ? "Такая категория уже есть."
          : "Не получилось создать. Попробуй ещё раз.",
      ))
      .finally(() => setBusy(false));
  };

  return (
    <Sheet title={kind === "expense" ? "Новая категория траты" : "Новая категория дохода"}
           onClose={onClose}>
      <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                    marginBottom: "var(--s-2)" }}>
        Название
      </div>
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={64}
             autoFocus aria-label="Название категории"
             placeholder={kind === "expense" ? "например, кальян" : "например, кэшбэк"}
             style={{ width: "100%", minHeight: "var(--tap)", background: "var(--surface-2)",
                      border: "none", borderRadius: "var(--r-field)", padding: "12px 14px",
                      color: "var(--text)", font: "inherit", fontSize: "var(--f-body)" }} />

      {kind === "expense" && (<>
        <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                      margin: "var(--s-4) 0 var(--s-2)" }}>
          Тип — из него считается разбивка 50/30/20
        </div>
        <div style={{ display: "flex", gap: "var(--s-2)" }}>
          {BUCKETS.map((b) => {
            const on = b.key === bucket;
            return (
              <button key={b.key} type="button" aria-pressed={on}
                      onClick={() => { tap(); setBucket(on ? null : b.key); }}
                      style={{ flexGrow: 1, minHeight: 40, borderRadius: "var(--r-sm)",
                               border: "none", cursor: "pointer", font: "inherit",
                               fontSize: "var(--f-caption)", fontWeight: on ? 600 : 400,
                               background: on ? "var(--accent)" : "var(--surface-2)",
                               color: on ? "var(--on-accent)" : "var(--text)" }}>
                {b.label}
              </button>
            );
          })}
        </div>
        {/* Тип можно не задавать: «прочее» честнее оставить неразмеченным,
            чем приписать к нужному ради красивой доли. */}
        <p style={hint}>Можно не выбирать — потом поставите в самой категории.</p>
      </>)}

      {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}

      <button type="button" disabled={busy || !name.trim()} onClick={create}
              style={{ width: "100%", minHeight: "var(--tap)", marginTop: "var(--s-4)",
                       padding: 14, borderRadius: "var(--r-field)", border: "none",
                       cursor: "pointer", font: "inherit", fontSize: "var(--f-body)",
                       fontWeight: 600, background: "var(--accent)", color: "var(--on-accent)",
                       opacity: busy || !name.trim() ? 0.5 : 1 }}>
        Создать
      </button>
    </Sheet>
  );
}

function CategorySheet({ category, kind, budget, siblings, onClose, onDone, onLimitChanged }: {
  category: CategoryFull;
  kind: "expense" | "income";
  siblings: CategoryFull[];
  budget: Budget | null;
  onClose: () => void;
  onDone: () => void;
  onLimitChanged: () => void;
}) {
  // Своё состояние категории: цвет и значок меняются, не закрывая шит, и без
  // этого выбранный цвет не подсветился бы до повторного открытия.
  const [current, setCurrent] = useState(category);
  const [name, setName] = useState(category.name);
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");

  const run = (patch: CategoryPatch, close = true) => {
    if (busy) return;
    tap();
    setBusy(true);
    setTrouble("");
    api.patchCategory(category.id, patch)
      .then((updated) => (close ? onDone() : setCurrent(updated)))
      .catch((e) => setTrouble(
        e instanceof ApiError && e.kind === "taken"
          ? "Категория с таким именем уже есть."
          : "Не получилось сохранить. Попробуй ещё раз.",
      ))
      .finally(() => setBusy(false));
  };

  return (
    <Sheet title={category.name} onClose={onClose}>
      <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                    marginBottom: "var(--s-2)" }}>
        Название
      </div>
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={64}
             aria-label="Название категории"
             style={{ width: "100%", minHeight: "var(--tap)", background: "var(--surface-2)",
                      border: "none", borderRadius: "var(--r-field)", padding: "12px 14px",
                      color: "var(--text)", font: "inherit", fontSize: "var(--f-body)",
                       }} />

      {/* Разбивка 50/30/20 делит ТРАТЫ (см. app/domain/methodology.py): доход в
          неё не входит вовсе, поэтому у доходной категории типа нет. Лимит и
          подсказка про неразмеченные уже были ограничены расходами — блок типа
          нет, и он протекал на вкладку доходов. */}
      {kind === "expense" && (<>
      <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                    margin: "var(--s-4) 0 var(--s-2)" }}>
        Тип — из него считается разбивка 50/30/20
      </div>
      <div style={{ display: "flex", gap: "var(--s-2)" }}>
        {BUCKETS.map((b) => {
          const on = b.key === category.bucket;
          return (
            <button key={b.key} type="button" aria-disabled={busy || undefined} aria-pressed={on}
                    onClick={() => run({ bucket: b.key })}
                    style={{ flexGrow: 1, minHeight: 40, borderRadius: "var(--r-sm)",
                             border: "none", cursor: "pointer", font: "inherit",
                             fontSize: "var(--f-caption)", fontWeight: on ? 600 : 400,
                             background: on ? "var(--accent)" : "var(--surface-2)",
                             color: on ? "var(--on-accent)" : "var(--text)" }}>
              {b.label}
            </button>
          );
        })}
      </div>
      {category.bucket && (
        <button type="button" aria-disabled={busy || undefined} onClick={() => run({ clear_bucket: true })}
                style={{ background: "none", border: "none", cursor: "pointer", font: "inherit",
                         fontSize: "var(--f-caption)", color: "var(--accent)",
                         padding: "10px 0", minHeight: 38 }}>
          Снять тип
        </button>
      )}
      </>)}

      {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}

      <button type="button" disabled={busy || !name.trim() || name.trim() === category.name}
              onClick={() => run({ name: name.trim() })}
              style={{ width: "100%", minHeight: "var(--tap)", marginTop: "var(--s-4)",
                       padding: 14, borderRadius: "var(--r-field)", border: "none",
                       cursor: "pointer", font: "inherit", fontSize: "var(--f-body)",
                       fontWeight: 600, background: "var(--accent)", color: "var(--on-accent)" }}>
        Сохранить название
      </button>
      <AppearanceBlock category={current} busy={busy} onPick={run} />

      {kind === "expense" && (
        <LimitBlock category={category} budget={budget} onChanged={onLimitChanged} />
      )}

      {category.is_seed ? (
        <p style={hint}>
          Эта категория была здесь с самого начала: переименовать можно, удалить — нет. На ней держатся
          привязки мест и вся история.
        </p>
      ) : (
        <DeleteBlock category={category} siblings={siblings} onDone={onDone} />
      )}
    </Sheet>
  );
}

/** Удаление своей категории — только вместе с переносом.
 *
 * Вопрос не «удалить?», а «куда перенести?». На категории висят операции,
 * подписки и выученные привязки мерчантов: каскад стёр бы историю, а отказ базы
 * (RESTRICT) оставил бы кнопку, которая не работает. Поэтому цель обязательна, и
 * пока она не выбрана, удалять нечем.
 */
function DeleteBlock({ category, siblings, onDone }: {
  category: CategoryFull; siblings: CategoryFull[]; onDone: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [into, setInto] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");

  if (siblings.length === 0) {
    return (
      <p style={hint}>
        Своя категория. Удалить её пока некуда: перенести операции не во что —
        других категорий этого вида нет.
      </p>
    );
  }

  const remove = () => {
    if (busy) return;
    if (into === null) return;
    tap();
    setBusy(true);
    setTrouble("");
    api.deleteCategory(category.id, into)
      .then(onDone)
      .catch(() => setTrouble("Не получилось удалить. Попробуй ещё раз."))
      .finally(() => setBusy(false));
  };

  if (!open) {
    return (
      <button type="button" onClick={() => { tap(); setOpen(true); }}
              style={{ background: "none", border: "none", cursor: "pointer", font: "inherit",
                       fontSize: "var(--f-caption)", color: "var(--danger)",
                       padding: "14px 0", minHeight: 44, marginTop: "var(--s-4)" }}>
        Удалить категорию
      </button>
    );
  }

  return (
    <div style={{ marginTop: "var(--s-5)" }}>
      <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                    marginBottom: "var(--s-2)" }}>
        Куда перенести операции этой категории
      </div>
      <div role="radiogroup" onKeyDown={onRadioKeys} aria-label="Категория для переноса"
           style={{ display: "flex", flexWrap: "wrap", gap: "var(--s-2)" }}>
        {siblings.map((c) => (
          <button key={c.id} type="button" role="radio" aria-checked={into === c.id}
                  aria-disabled={busy || undefined} onClick={() => setInto(c.id)}
                  style={{ minHeight: 38, padding: "0 12px", borderRadius: "var(--r-pill)",
                           cursor: "pointer", font: "inherit", fontSize: "var(--f-caption)",
                           background: "var(--surface-2)", color: "var(--text)",
                           border: into === c.id
                             ? "2px solid var(--accent)" : "2px solid transparent" }}>
            {c.name}
          </button>
        ))}
      </div>
      {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}
      <div style={{ display: "flex", gap: "var(--s-2)", marginTop: "var(--s-4)" }}>
        <button type="button" disabled={busy || into === null} onClick={remove}
                style={{ flexGrow: 1, minHeight: "var(--tap)", padding: 14, border: "none",
                         borderRadius: "var(--r-field)", cursor: "pointer", font: "inherit",
                         fontSize: "var(--f-body)", fontWeight: 600,
                         background: into === null ? "var(--surface-2)" : "var(--danger)",
                         color: into === null ? "var(--text-disabled)" : "var(--on-accent)" }}>
          Перенести и удалить
        </button>
        <button type="button" onClick={() => { setOpen(false); setInto(null); }}
                style={{ flexGrow: 1, minHeight: "var(--tap)", padding: 14, border: "none",
                         borderRadius: "var(--r-field)", cursor: "pointer", font: "inherit",
                         fontSize: "var(--f-body)", fontWeight: 600,
                         background: "var(--surface-2)", color: "var(--text)" }}>
          Оставить
        </button>
      </div>
      <p style={hint}>
        Операции, подписки и запомненные места переедут в выбранную категорию.
        Лимит этой категории снимется: складывать два лимита в один нельзя.
      </p>
    </div>
  );
}

/** Цвет и значок категории (MA-FR-26..28).
 *
 * Палитра закрытая: произвольный цвет проехал бы мимо тем и контраста, за
 * которые отвечают токены. Восемь оттенков — ровно те, которыми рисуется пирог.
 *
 * Ничего не выбрано — не пустое состояние, а «как было»: цвет тогда идёт по
 * рангу трат, значок подбирается по названию. Поэтому «Автоматически» — такой
 * же вариант выбора, а не отсутствие выбора.
 */
function AppearanceBlock({ category, busy, onPick }: {
  category: CategoryFull;
  busy: boolean;
  onPick: (patch: CategoryPatch, close?: boolean) => void;
}) {
  const [openIcons, setOpenIcons] = useState(false);
  // Пока цвет не выбран, глиф рисуем основным цветом текста: серым по тёмной
  // плитке значки в сетке не разглядеть, а выбирают их именно по рисунку.
  const swatch = category.color ? `var(${category.color})` : "var(--text)";

  return (
    <>
      <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                    margin: "var(--s-5) 0 var(--s-2)" }}>
        Цвет — в пироге и в списках
      </div>
      <div role="radiogroup" onKeyDown={onRadioKeys} aria-label="Цвет категории"
           style={{ display: "flex", flexWrap: "wrap", gap: "var(--s-2)" }}>
        {COLORS.map((token) => {
          const on = category.color === token;
          return (
            <button key={token} type="button" role="radio" aria-checked={on} aria-disabled={busy || undefined}
                    aria-label={`Цвет ${token.replace("--cat-", "")}`}
                    onClick={() => onPick({ color: token }, false)}
                    style={{ width: 38, height: 38, borderRadius: "50%", cursor: "pointer",
                             background: `var(${token})`, padding: 0,
                             border: on ? "3px solid var(--text)" : "3px solid transparent",
                              }} />
          );
        })}
        <button type="button" role="radio" aria-checked={category.color === null} aria-disabled={busy || undefined}
                onClick={() => onPick({ color: "" }, false)}
                style={{ minHeight: 38, padding: "0 12px", borderRadius: "var(--r-pill)",
                         cursor: "pointer", font: "inherit", fontSize: "var(--f-caption)",
                         background: "var(--surface-2)", color: "var(--text)",
                         border: category.color === null
                           ? "2px solid var(--accent)" : "2px solid transparent" }}>
          Автоматически
        </button>
      </div>

      <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                    margin: "var(--s-4) 0 var(--s-2)" }}>
        Значок
      </div>
      <button type="button" onClick={() => { tap(); setOpenIcons(!openIcons); }}
              aria-expanded={openIcons}
              style={{ display: "flex", alignItems: "center", gap: "var(--s-3)", width: "100%",
                       minHeight: "var(--tap)", background: "var(--surface-2)", border: "none",
                       borderRadius: "var(--r-field)", padding: "8px 14px", cursor: "pointer",
                       font: "inherit", color: "inherit", textAlign: "left" }}>
        <CategoryIcon name={category.name} icon={category.icon} color={swatch} size={32} />
        <span style={{ flexGrow: 1, fontSize: "var(--f-body-s)" }}>
          {category.icon ?? "по названию"}
        </span>
        <span style={{ fontSize: "var(--f-caption)", color: "var(--accent)" }}>
          {openIcons ? "Свернуть" : "Выбрать"}
        </span>
      </button>

      {openIcons && (
        <div role="radiogroup" onKeyDown={onRadioKeys} aria-label="Значок категории"
             style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(46px, 1fr))",
                      gap: "var(--s-2)", marginTop: "var(--s-2)" }}>
          {ICON_KEYS.map((key) => {
            const on = category.icon === key;
            return (
              <button key={key} type="button" role="radio" aria-checked={on} aria-disabled={busy || undefined}
                      aria-label={key} onClick={() => onPick({ icon: key }, false)}
                      style={{ display: "flex", alignItems: "center", justifyContent: "center",
                               minHeight: 46, borderRadius: "var(--r-sm)", cursor: "pointer",
                               background: on ? "var(--accent-tint)" : "var(--surface-2)",
                               border: on ? "2px solid var(--accent)" : "2px solid transparent",
                               padding: 0 }}>
                <CategoryIcon name={key} icon={key} color={swatch} size={30} />
              </button>
            );
          })}
          <button type="button" role="radio" aria-checked={category.icon === null} aria-disabled={busy || undefined}
                  onClick={() => onPick({ icon: "" }, false)}
                  style={{ gridColumn: "1 / -1", minHeight: 40, borderRadius: "var(--r-sm)",
                           cursor: "pointer", font: "inherit", fontSize: "var(--f-caption)",
                           background: "var(--surface-2)", color: "var(--text)",
                           border: category.icon === null
                             ? "2px solid var(--accent)" : "2px solid transparent" }}>
            Подбирать по названию
          </button>
        </div>
      )}
    </>
  );
}

/** Палитра выбора — семь, а не восемь.
 *
 * `--cat-8` (#D9536F) и `--cat-4` (#F2748C) — розовые близнецы: рядом в сетке их
 * не различить, и человек не понимает, какой выбрал. В пироге они и так никогда
 * не встречаются (раскладка по рангу берёт шесть разведённых), а в выборе
 * встретились бы всегда. Восьмой остаётся в токенах — просто не предлагается.
 *
 * Порядок — по максимальной разведённости соседей, как в пироге.
 */
const COLORS = ["--cat-1", "--cat-3", "--cat-2", "--cat-7",
                "--cat-4", "--cat-6", "--cat-5"];

/** Мягкий лимит категории (FR-59..61).
 *
 * «Мягкий» — не запрет: превышение ничего не блокирует и ни в чём не обвиняет,
 * оно просто видно (ADR-0002). Поэтому здесь полоска и число, а не предупреждение.
 */
function LimitBlock({ category, budget, onChanged }: {
  category: CategoryFull; budget: Budget | null; onChanged: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [busy, setBusy] = useState(false);
  const [trouble, setTrouble] = useState("");

  const save = () => {
    if (busy || !amount.trim()) return;
    tap();
    setBusy(true);
    setTrouble("");
    api.setBudget(category.id, amount.trim(), currency)
      .then(() => { setAmount(""); onChanged(); })
      .catch(() => setTrouble("Не получилось. Проверь сумму."))
      .finally(() => setBusy(false));
  };

  const drop = () => {
    if (busy) return;
    tap();
    setBusy(true);
    api.dropBudget(category.id).then(onChanged)
      .catch(() => setTrouble("Не получилось снять лимит. Попробуй ещё раз."))
      .finally(() => setBusy(false));
  };

  const share = budget
    ? Math.min(Number(budget.spent) / Math.max(Number(budget.limit), 1e-9), 1.4)
    : 0;
  const ink = budget?.status === "over" ? "var(--danger)"
    : budget?.status === "near" ? "var(--warn)" : "var(--ok)";

  return (
    <>
      <div style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)",
                    margin: "var(--s-5) 0 var(--s-2)" }}>
        Лимит в месяц
      </div>

      {budget && (
        <div style={{ marginBottom: "var(--s-3)" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span className="num" style={{ fontSize: "var(--f-body)", fontWeight: 600 }}>
              {formatMoney(budget.spent, budget.base_currency)}
            </span>
            <span className="num" style={{ fontSize: "var(--f-caption)", color: "var(--text-muted)" }}>
              из {formatMoney(budget.limit, budget.base_currency)}
            </span>
          </div>
          {/* Ширина полоски — единственное место, где деньги становятся числом:
              это пиксели, а не арифметика над суммой (MA-INV-6). */}
          <div style={{ height: 6, borderRadius: 3, background: "var(--surface-3)",
                        marginTop: 8, overflow: "hidden" }}>
            <div style={{ width: `${Math.min(share, 1) * 100}%`, height: "100%",
                          background: ink, borderRadius: 3 }} />
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: "var(--s-2)" }}>
        <input value={amount} onChange={(e) => setAmount(e.target.value)} inputMode="decimal"
               placeholder={budget ? "новая сумма" : "например 200"}
               aria-label="Сумма лимита"
               style={{ flexGrow: 1, minWidth: 0, minHeight: "var(--tap)",
                        background: "var(--surface-2)", border: "none",
                        borderRadius: "var(--r-field)", padding: "12px 14px",
                        color: "var(--text)", font: "inherit", fontSize: "var(--f-body)" }} />
        <select value={currency} onChange={(e) => setCurrency(e.target.value)}
                aria-label="Валюта лимита"
                style={{ minHeight: "var(--tap)", background: "var(--surface-2)",
                         border: "none", borderRadius: "var(--r-field)", padding: "0 12px",
                         color: "var(--text)", font: "inherit", fontSize: "var(--f-body)" }}>
          <option value="USD">$</option>
          <option value="VND">₫</option>
          <option value="RUB">₽</option>
        </select>
        {/* Остаётся на экране, пока идёт запрос: `disabled` тут уронил бы фокус
            в body. Повторное нажатие отбивает `save`. */}
        <button type="button" aria-disabled={busy || !amount.trim() || undefined}
                onClick={save}
                style={{ minHeight: "var(--tap)", padding: "0 16px", border: "none",
                         borderRadius: "var(--r-field)", cursor: "pointer", font: "inherit",
                         fontSize: "var(--f-body)", fontWeight: 600,
                         background: "var(--accent)", color: "var(--on-accent)" }}>
          {budget ? "Изменить" : "Задать"}
        </button>
      </div>
      {budget && (
        <button type="button" aria-disabled={busy || undefined} onClick={drop}
                style={{ background: "none", border: "none", cursor: "pointer", font: "inherit",
                         fontSize: "var(--f-caption)", color: "var(--accent)",
                         padding: "10px 0", minHeight: 38 }}>
          Снять лимит
        </button>
      )}
      {trouble && <p role="alert" style={{ ...hint, color: "var(--warn)" }}>{trouble}</p>}
      <p style={hint}>
        Лимит хранится в базовой валюте по курсу дня. Он ничего не запрещает — только
        показывает, где месяц идёт быстрее обычного.
      </p>
    </>
  );
}

function Skeleton() {
  return (
    <div style={card} role="status" aria-busy="true" aria-label="Загружаю категории">
      <div style={{ height: 11, width: "45%", background: "var(--surface-3)", borderRadius: 5 }} />
    </div>
  );
}



function categoryColorOf(row: CategoryFull, rows: CategoryFull[]): string {
  // Выбранный цвет держится за категорией; не выбран — берём стабильный по
  // позиции, чтобы иконки не были одинаково серыми.
  return row.color ? `var(${row.color})` : nthColor(rows.findIndex((c) => c.id === row.id));
}
